import React, { useState, useEffect } from "react";
import {
  Building2,
  Hash,
  CreditCard,
  MapPin,
  Plus,
  Edit2,
  Trash2,
  User,
  Calendar,
  Mail,
  Save,
  Check,
  Globe,
  Shield,
  Phone,
} from "lucide-react";
import { orgService } from "../../services/orgService";
import { getEngineSettings, updateEngineSettings } from "../../services/cadenceService";
import FormControl from "../common/FormControl";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Toast, { ToastMessage } from "../common/Toast";
import { confirmDelete } from "../common/confirm";
import ApprovalDot from "../common/ApprovalDot";
import WorkflowModal from "../common/WorkflowModal";
import { usePermission } from "../../contexts/PermissionContext";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";
import NoDataFound from "../common/NoDataFound";
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../validation/ValidatedFields';

const RBAC_ACTIVITY = 'ORG_ENTITY_PROFILE';

// --- Interfaces ---

interface OrgDetails {
  id?: number;
  name: string;
  code: string;
  cin: string;
  pan: string;
  incorporation_date: string;
  constitution: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  address: string;
  corporate_address: string;
}

interface GstRecord {
  id: number;
  entity_id: number;
  state: string;
  gstin: string;
  auth_person: string;
  auth_phone: string;
  auth_email: string;
  reg_date: string;
  address: string;
  workflow_state?: string;
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
}

interface TdsRecord {
  id: number;
  entity_id: number;
  tan: string;
  circle: string;
  auth_person: string;
  auth_phone: string;
  auth_email: string;
  reg_date: string;
  workflow_state?: string;
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
}

interface OtherRegRecord {
  id: number;
  entity_id: number;
  authority: string;
  licensee: string;
  purpose: string;
  issue_date: string;
  expiry_date: string;
  workflow_state?: string;
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
}

const OrganizationProfile: React.FC = () => {
  const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
  const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
  const canView = viewPerm.allowed;
  const canModify = modifyPerm.allowed;
  const permsLoading = viewPerm.loading;
  const { formatDate } = useEntitySettings();
  // Note: approve/reject/reopen gating is resolved by the WF engine inside
  // <WorkflowModal> (it returns per-action permissions), so no canApprove here.

  type WfTarget =
    | { type: 'gst'; record: GstRecord }
    | { type: 'tds'; record: TdsRecord }
    | { type: 'reg'; record: OtherRegRecord };

  const [workflowOpenFor, setWorkflowOpenFor] = useState<WfTarget | null>(null);

  // --- 1. ORGANISATION DETAILS STATE ---
  const [orgDetails, setOrgDetails] = useState<OrgDetails>({
    name: "",
    code: "",
    cin: "",
    pan: "",
    incorporation_date: "",
    constitution: "",
    contact_person: "",
    contact_phone: "",
    contact_email: "",
    address: "",
    corporate_address: "",
  });
  const [isProfileModalOpen, setProfileModalOpen] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  // Per-entity cadence scheduler opt-in (sch_engine_settings.is_enabled), edited in the profile modal.
  const [cadenceEnabled, setCadenceEnabled] = useState(false);
  const [cadenceFormEnabled, setCadenceFormEnabled] = useState(false);
  const [profileFormData, setProfileFormData] = useState<Partial<OrgDetails>>(
    {},
  );

  // --- 2. GST STATE & LOGIC ---
  const [gstList, setGstList] = useState<GstRecord[]>([]);
  const [isGstModalOpen, setGstModalOpen] = useState(false);
  const [editingGstId, setEditingGstId] = useState<number | null>(null);
  const [gstFormEnabled, setGstFormEnabled] = useState(false);
  const [gstFormData, setGstFormData] = useState<Partial<GstRecord>>({
    state: "",
    gstin: "",
    auth_person: "",
    auth_phone: "",
    auth_email: "",
    reg_date: "",
    address: "",
  });

  // --- 3. TDS STATE & LOGIC ---
  const [tdsList, setTdsList] = useState<TdsRecord[]>([]);
  const [isTdsModalOpen, setTdsModalOpen] = useState(false);
  const [editingTdsId, setEditingTdsId] = useState<number | null>(null);
  const [tdsFormEnabled, setTdsFormEnabled] = useState(false);
  const [tdsFormData, setTdsFormData] = useState<Partial<TdsRecord>>({
    tan: "",
    circle: "",
    auth_person: "",
    auth_phone: "",
    auth_email: "",
    reg_date: "",
  });

  // --- 4. OTHER REGISTRATIONS STATE & LOGIC ---
  const [otherRegList, setOtherRegList] = useState<OtherRegRecord[]>([]);
  const [isOtherRegModalOpen, setOtherRegModalOpen] = useState(false);
  const [editingOtherRegId, setEditingOtherRegId] = useState<number | null>(
    null,
  );
  const [otherRegFormEnabled, setOtherRegFormEnabled] = useState(false);
  const [otherRegFormData, setOtherRegFormData] = useState<
    Partial<OtherRegRecord>
  >({
    authority: "",
    licensee: "",
    purpose: "",
    issue_date: "",
    expiry_date: "",
  });

  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [submittingProfile, setSubmittingProfile] = useState(false);
  const [submittingGst, setSubmittingGst] = useState(false);
  const [submittingTds, setSubmittingTds] = useState(false);
  const [submittingOtherReg, setSubmittingOtherReg] = useState(false);

  // Shared validation engine — MAIN profile form
  const profileVh = useFieldValidation();
  // Shared validation engine — sub-forms
  const gstVh = useFieldValidation();
  const tdsVh = useFieldValidation();
  const regVh = useFieldValidation();


  useEffect(() => {
    if (canView) fetchData();
  }, [canView]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profile, gst, tds, regs, engine] = await Promise.all([
        orgService.getProfile(),
        orgService.getEntityGst(),
        orgService.getEntityTds(),
        orgService.getEntityRegistrations(),
        getEngineSettings().catch(() => null),   // tolerate missing perms / no row
      ]);
      setOrgDetails(profile);
      setGstList(gst);
      setTdsList(tds);
      setOtherRegList(regs);
      setCadenceEnabled(!!engine && Number(engine.is_enabled) === 1);
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Workflow (submit/approve/reject/reopen) for GST/TDS/other registrations is
  // now driven end-to-end by the unified <WorkflowModal> via the /api/v1/wf
  // engine (kinds org_entity_gst / org_entity_tds / org_entity_registration).
  const closeWfModal = () => setWorkflowOpenFor(null);

  if (!permsLoading && !canView) {
    return (
      <NoDataFound
        title="Not authorised"
        message={viewPerm.message || 'You are not authorised to view Organisation Profile. Please contact your Super Admin.'}
      />
    );
  }

  const openEditProfileModal = () => {
    setProfileFormData({ ...orgDetails });
    setCadenceFormEnabled(cadenceEnabled);
    profileVh.reset();
    setIsEditingProfile(false);
    setProfileModalOpen(true);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    const valid = profileVh.validateAll([
      { name: 'name', code: 101, value: profileFormData.name, required: true },
      { name: 'code', code: 501, value: profileFormData.code, required: true },
      { name: 'incorporation_date', code: 305, value: profileFormData.incorporation_date },
      { name: 'constitution', code: 101, value: profileFormData.constitution },
      { name: 'cin', code: 511, value: profileFormData.cin, required: true },
      { name: 'pan', code: 510, value: profileFormData.pan },
      { name: 'contact_person', code: 102, value: profileFormData.contact_person },
      { name: 'contact_phone', code: 405, value: profileFormData.contact_phone },
      { name: 'contact_email', code: 401, value: profileFormData.contact_email },
      { name: 'address', code: 101, value: profileFormData.address, required: true },
      { name: 'corporate_address', code: 101, value: profileFormData.corporate_address },
    ]);
    if (!valid) return;
    setSubmittingProfile(true);
    try {
      const payload: Record<string, any> = { ...profileFormData };
      if (payload.incorporation_date === "") payload.incorporation_date = null;
      const updated = await orgService.updateProfile(payload);
      setOrgDetails(updated);
      // Persist the per-entity cadence opt-in (separate table) only when changed.
      if (cadenceFormEnabled !== cadenceEnabled) {
        await updateEngineSettings({ is_enabled: cadenceFormEnabled ? 1 : 0 });
        setCadenceEnabled(cadenceFormEnabled);
      }
      setIsEditingProfile(false);
      setProfileModalOpen(false);
      setToast({ type: "success", message: "Profile updated successfully." });
    } catch (error: any) {
      console.error("Error updating profile:", error);
      const detail = error.response?.data?.detail;
      let msg: string;
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d: any) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join("; ");
      else if (detail) msg = JSON.stringify(detail);
      else msg = error.message || "Unknown error";
      setToast({
        type: "error",
        message: "Failed to update profile: " + msg,
      });
    } finally {
      setSubmittingProfile(false);
    }
  };

  const openAddGstModal = () => {
    setGstFormData({
      state: "",
      gstin: "",
      auth_person: "",
      auth_phone: "",
      auth_email: "",
      reg_date: "",
      address: "",
    });
    gstVh.reset();
    setEditingGstId(null);
    setGstModalOpen(true);
  };

  const openEditGstModal = (gst: GstRecord) => {
    setGstFormData(gst);
    gstVh.reset();
    setEditingGstId(gst.id);
    setGstFormEnabled(false);
    setGstModalOpen(true);
  };

  const handleSaveGst = async (e: React.FormEvent) => {
    e.preventDefault();
    const valid = gstVh.validateAll([
      { name: 'gstin', code: 512, value: gstFormData.gstin, required: true },
      { name: 'state', code: 101, value: gstFormData.state, required: true },
      { name: 'auth_person', code: 102, value: gstFormData.auth_person, required: true },
      { name: 'reg_date', code: 305, value: gstFormData.reg_date, required: true },
      { name: 'auth_phone', code: 405, value: gstFormData.auth_phone },
      { name: 'auth_email', code: 401, value: gstFormData.auth_email },
      { name: 'address', code: 101, value: gstFormData.address },
    ]);
    if (!valid) return;
    setSubmittingGst(true);
    try {
      const payload: Record<string, any> = { ...gstFormData };
      if (payload.reg_date === "") payload.reg_date = null;
      if (editingGstId) {
        const updated = await orgService.updateEntityGst(editingGstId, payload);
        setGstList(
          gstList.map((item) => (item.id === editingGstId ? updated : item)),
        );
        setToast({
          type: "success",
          message: "GST registration updated successfully.",
        });
      } else {
        const created = await orgService.createEntityGst(payload);
        setGstList([...gstList, created]);
        setToast({
          type: "success",
          message: "GST registration added successfully.",
        });
      }
      setGstModalOpen(false);
    } catch (error: any) {
      console.error("Error saving GST:", error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : JSON.stringify(detail);
      setToast({
        type: "error",
        message: "Failed to save GST: " + (msg || error.message),
      });
    } finally {
      setSubmittingGst(false);
    }
  };

  const handleDeleteGst = async (id: number) => {
    const ok = await confirmDelete({
      text: "This will permanently delete the GST registration.",
    });
    if (!ok) return;
    try {
      await orgService.deleteEntityGst(id);
      setGstList(gstList.filter((item) => item.id !== id));
      setToast({ type: "delete", message: "GST registration deleted." });
    } catch (error) {
      console.error("Error deleting GST:", error);
      setToast({
        type: "error",
        message: "Failed to delete GST registration.",
      });
    }
  };

  const openAddTdsModal = () => {
    setTdsFormData({
      tan: "",
      circle: "",
      auth_person: "",
      auth_phone: "",
      auth_email: "",
      reg_date: "",
    });
    tdsVh.reset();
    setEditingTdsId(null);
    setTdsModalOpen(true);
  };

  const openEditTdsModal = (tds: TdsRecord) => {
    setTdsFormData(tds);
    tdsVh.reset();
    setEditingTdsId(tds.id);
    setTdsFormEnabled(false);
    setTdsModalOpen(true);
  };

  const handleSaveTds = async (e: React.FormEvent) => {
    e.preventDefault();
    const valid = tdsVh.validateAll([
      { name: 'tan', code: 513, value: tdsFormData.tan, required: true },
      { name: 'circle', code: 101, value: tdsFormData.circle, required: true },
      { name: 'auth_person', code: 102, value: tdsFormData.auth_person, required: true },
      { name: 'reg_date', code: 305, value: tdsFormData.reg_date, required: true },
      { name: 'auth_phone', code: 405, value: tdsFormData.auth_phone },
      { name: 'auth_email', code: 401, value: tdsFormData.auth_email },
    ]);
    if (!valid) return;
    setSubmittingTds(true);
    try {
      const payload: Record<string, any> = { ...tdsFormData };
      if (payload.reg_date === "") payload.reg_date = null;
      if (editingTdsId) {
        const updated = await orgService.updateEntityTds(editingTdsId, payload);
        setTdsList(
          tdsList.map((item) => (item.id === editingTdsId ? updated : item)),
        );
        setToast({
          type: "success",
          message: "TDS detail updated successfully.",
        });
      } else {
        const created = await orgService.createEntityTds(payload);
        setTdsList([...tdsList, created]);
        setToast({
          type: "success",
          message: "TDS detail added successfully.",
        });
      }
      setTdsModalOpen(false);
    } catch (error: any) {
      console.error("Error saving TDS:", error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : JSON.stringify(detail);
      setToast({
        type: "error",
        message: "Failed to save TDS: " + (msg || error.message),
      });
    } finally {
      setSubmittingTds(false);
    }
  };

  const handleDeleteTds = async (id: number) => {
    const ok = await confirmDelete({
      text: "This will permanently delete the TDS detail.",
    });
    if (!ok) return;
    try {
      await orgService.deleteEntityTds(id);
      setTdsList(tdsList.filter((item) => item.id !== id));
      setToast({ type: "delete", message: "TDS detail deleted." });
    } catch (error) {
      console.error("Error deleting TDS:", error);
      setToast({ type: "error", message: "Failed to delete TDS detail." });
    }
  };

  const openAddOtherRegModal = () => {
    setOtherRegFormData({
      authority: "",
      licensee: "",
      purpose: "",
      issue_date: "",
      expiry_date: "",
    });
    regVh.reset();
    setEditingOtherRegId(null);
    setOtherRegModalOpen(true);
  };

  const openEditOtherRegModal = (reg: OtherRegRecord) => {
    setOtherRegFormData(reg);
    regVh.reset();
    setEditingOtherRegId(reg.id);
    setOtherRegFormEnabled(false);
    setOtherRegModalOpen(true);
  };

  const handleSaveOtherReg = async (e: React.FormEvent) => {
    e.preventDefault();
    const specs: any[] = [
      { name: 'authority', code: 101, value: otherRegFormData.authority, required: true },
      { name: 'licensee', code: 101, value: otherRegFormData.licensee, required: true },
      { name: 'purpose', code: 101, value: otherRegFormData.purpose, required: true },
      { name: 'issue_date', code: 305, value: otherRegFormData.issue_date, required: true },
      { name: 'expiry_date', code: 305, value: otherRegFormData.expiry_date },
    ];
    if (otherRegFormData.issue_date && otherRegFormData.expiry_date) {
      specs.push({
        name: 'expiry_date', code: 601, value: 'x', required: true,
        additional: { from_date: otherRegFormData.issue_date, to_date: otherRegFormData.expiry_date },
      });
    }
    const valid = regVh.validateAll(specs);
    if (!valid) return;
    setSubmittingOtherReg(true);
    try {
      const payload: Record<string, any> = { ...otherRegFormData };
      if (payload.issue_date === "") payload.issue_date = null;
      if (payload.expiry_date === "") payload.expiry_date = null;
      if (editingOtherRegId) {
        const updated = await orgService.updateEntityRegistration(
          editingOtherRegId,
          payload,
        );
        setOtherRegList(
          otherRegList.map((item) =>
            item.id === editingOtherRegId ? updated : item,
          ),
        );
        setToast({
          type: "success",
          message: "Registration updated successfully.",
        });
      } else {
        const created = await orgService.createEntityRegistration(payload);
        setOtherRegList([...otherRegList, created]);
        setToast({
          type: "success",
          message: "Registration added successfully.",
        });
      }
      setOtherRegModalOpen(false);
    } catch (error: any) {
      console.error("Error saving registration:", error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : JSON.stringify(detail);
      setToast({
        type: "error",
        message: "Failed to save registration: " + (msg || error.message),
      });
    } finally {
      setSubmittingOtherReg(false);
    }
  };

  const handleDeleteOtherReg = async (id: number) => {
    const ok = await confirmDelete({
      text: "This will permanently delete the registration.",
    });
    if (!ok) return;
    try {
      await orgService.deleteEntityRegistration(id);
      setOtherRegList(otherRegList.filter((item) => item.id !== id));
      setToast({ type: "delete", message: "Registration deleted." });
    } catch (error) {
      console.error("Error deleting registration:", error);
      setToast({ type: "error", message: "Failed to delete registration." });
    }
  };

  return (
    <>
      {/* 1. Organisation Details (One-to-One) */}
      <div className="netbody-card flex flex-col h-full ">
        <div className="netbody-header" style={{ justifyContent: "flex-end" }}>
          {canModify && (
            <Button variant="fill" icon={Edit2} onClick={openEditProfileModal} data-testid="BTN_org_profile_edit">
              Edit
            </Button>
          )}
        </div>

        <div className="netbody-content">
          <div className="scroll-container">
            {loading ? (
              <div className="skel-page">

                {/* Corporate Identity */}
                <div className="skel-card">
                  <div className="table-list-skeleton-bar skel-heading" />
                  <div className="skel-grid-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="skel-field">
                        <div className="table-list-skeleton-bar skel-field-label" />
                        <div className="table-list-skeleton-bar skel-field-value" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* GST / TDS sub-table */}
                <div className="skel-card">
                  <div className="table-list-skeleton-bar skel-heading" />
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="skel-row">
                      <div className="table-list-skeleton-bar skel-w-sm skel-h-text" />
                      <div className="table-list-skeleton-bar skel-w-md skel-h-text" />
                      <div className="table-list-skeleton-bar skel-h-icon" />
                    </div>
                  ))}
                </div>

                {/* Other Registrations */}
                <div className="skel-card">
                  <div className="table-list-skeleton-bar skel-heading" />
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="skel-row">
                      <div className="table-list-skeleton-bar skel-w-xs skel-h-text" />
                      <div className="table-list-skeleton-bar skel-w-xs skel-h-text" />
                      <div className="table-list-skeleton-bar skel-w-sm skel-h-text" />
                      <div className="table-list-skeleton-bar skel-w-xs skel-h-text" />
                      <div className="table-list-skeleton-bar skel-h-icon" />
                    </div>
                  ))}
                </div>

              </div>
            ) : (
              <>
                {/* Identity strip */}
                {/* <div className="org-profile-top">
                  <div className="org-profile-org-name">
                    {orgDetails.name || "—"}
                  </div>
                  <div className="org-profile-meta">
                    {orgDetails.constitution && (
                      <span className="org-profile-type-badge">
                        <Shield size={10} /> {orgDetails.constitution}
                      </span>
                    )}
                    {orgDetails.code && (
                      <span className="org-profile-id-chip">
                        <Globe size={10} /> {orgDetails.code}
                      </span>
                    )}
                  </div>
                </div> */}

                {/* Corporate Identity */}
                <div className="org-profile-section">
                  <div className="org-profile-section-label">
                    <Hash size={11} /> Corporate Identity
                  </div>
                  <div className="org-profile-grid">
                    <div className="org-profile-field">
                      <span className="org-profile-field-label">
                        <Hash size={12} /> CIN
                      </span>
                      <span
                        className={`org-profile-field-value ${orgDetails.cin ? "is-code" : "is-empty"}`}
                      >
                        {orgDetails.cin || "—"}
                      </span>
                    </div>
                    <div className="org-profile-field">
                      <span className="org-profile-field-label">
                        <CreditCard size={13} /> PAN
                      </span>
                      <span
                        className={`org-profile-field-value ${orgDetails.pan ? "is-code" : "is-empty"}`}
                      >
                        {orgDetails.pan || "—"}
                      </span>
                    </div>
                    <div className="org-profile-field">
                      <span className="org-profile-field-label">
                        <Calendar size={13} /> Incorporation Date
                      </span>
                      <span
                        className={`org-profile-field-value ${!orgDetails.incorporation_date ? "is-empty" : ""}`}
                      >
                        {orgDetails.incorporation_date ? formatDate(orgDetails.incorporation_date) : "—"}
                      </span>
                    </div>
                  </div>
                </div>

                <hr className="org-profile-divider" />

                {/* Contact */}
                <div className="org-profile-section">
                  <div className="org-profile-section-label">
                    <User size={11} /> Contact
                  </div>
                  <div className="org-profile-grid">
                    <div className="org-profile-field">
                      <span className="org-profile-field-label">
                        <User size={13} /> Contact Person
                      </span>
                      <span
                        className={`org-profile-field-value ${!orgDetails.contact_person ? "is-empty" : ""}`}
                      >
                        {orgDetails.contact_person || "—"}
                      </span>
                    </div>
                    <div className="org-profile-field">
                      <span className="org-profile-field-label">
                        <Phone size={13} /> Phone
                      </span>
                      <span
                        className={`org-profile-field-value ${!orgDetails.contact_phone ? "is-empty" : ""}`}
                      >
                        {orgDetails.contact_phone || "—"}
                      </span>
                    </div>
                    <div className="org-profile-field">
                      <span className="org-profile-field-label">
                        <Mail size={13} /> Email
                      </span>
                      <span
                        className={`org-profile-field-value ${!orgDetails.contact_email ? "is-empty" : ""}`}
                      >
                        {orgDetails.contact_email || "—"}
                      </span>
                    </div>
                  </div>
                </div>

                <hr className="org-profile-divider" />

                {/* Addresses */}
                <div className="org-profile-section">
                  <div className="org-profile-section-label">
                    <MapPin size={11} /> Addresses
                  </div>
                  <div className="org-profile-address-grid">
                    <div className="org-profile-address-card">
                      <div className="org-profile-address-title">
                        <MapPin size={13} /> Registered Office
                      </div>
                      <div
                        className={`org-profile-address-text ${!orgDetails.address ? "is-empty" : ""}`}
                      >
                        {orgDetails.address || "—"}
                      </div>
                    </div>
                    <div className="org-profile-address-card">
                      <div className="org-profile-address-title">
                        <MapPin size={13} /> Corporate Office
                      </div>
                      <div
                        className={`org-profile-address-text ${!orgDetails.corporate_address ? "is-empty" : ""}`}
                      >
                        {orgDetails.corporate_address || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cadence Engine — per-entity scheduler opt-in (edited via Edit Profile) */}
                <hr className="org-profile-divider" />
                <div className="org-profile-section">
                  <div className="org-profile-section-label">
                    <Calendar size={11} /> Cadence Engine
                  </div>
                  <div className="org-profile-address-text" data-testid="TXT_org_profile_cadence_status">
                    Automated cadence runs are{" "}
                    <strong style={{ color: cadenceEnabled ? "#166534" : "#b91c1c" }}>
                      {cadenceEnabled ? "ENABLED" : "DISABLED"}
                    </strong>{" "}
                    for this entity.{" "}
                    {cadenceEnabled
                      ? "The engine auto-fires scheduled obligations on their due dates."
                      : "Obligations are raised only via manual rollout until this is enabled."}{" "}
                    Change it in <strong>Edit Profile</strong>.
                  </div>
                </div>

                {/* Registrations & Tax */}
                <hr className="org-profile-divider" />
                <div className="org-profile-section">
                  <div className="org-profile-section-label">
                    <Shield size={11} /> Registrations &amp; Tax
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {/* GST Details (One-to-Many) */}
                      <div className="org-profile-sub-card">
                        <div className="org-profile-sub-header">
                          <span className="org-profile-sub-header-label">
                            GST Registrations
                          </span>
                          {canModify && (
                            <Button
                              variant="fill"
                              icon={Plus}
                              onClick={openAddGstModal}
                              className="btn-secondary-outline"
                              data-testid="BTN_org_profile_gst_create"
                            >
                              Create
                            </Button>
                          )}
                        </div>
                        <div className="org-profile-sub-body overflow-auto max-h-64">
                          <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-100">
                              <tr>
                                <th className="px-2 py-3 w-6"></th>
                                <th className="px-5 py-3 text-xs uppercase tracking-wider">
                                  State
                                </th>
                                <th className="px-5 py-3 text-xs uppercase tracking-wider">
                                  GSTIN
                                </th>
                                <th className="px-5 py-3 w-10"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {gstList.map((item) => (
                                <tr
                                  key={item.id}
                                  className="hover:bg-blue-50/30"
                                >
                                  <td
                                    className="px-2 py-3 w-6 cursor-pointer"
                                    onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor({ type: 'gst', record: item }); }}
                                    title="Workflow — view timeline & act"
                                    data-testid={`BTN_org_profile_gst_row_${item.id}_workflow`}
                                  >
                                    <ApprovalDot
                                      state={(item.workflow_state ?? 'draft') as any}
                                      onClick={() => setWorkflowOpenFor({ type: 'gst', record: item })}
                                    />
                                  </td>
                                  <td className="px-5 py-3 font-medium text-gray-700">
                                    {item.state}
                                  </td>
                                  <td className="px-5 py-3 text-gray-600 text-xs">
                                    <span className=" ">{item.gstin}</span>
                                  </td>
                                  <td
                                    className="px-5 py-3 text-right"
                                    style={{ whiteSpace: "nowrap" }}
                                  >
                                    {canModify && item.workflow_state !== 'approved' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditGstModal(item);
                                        }}
                                        className="table-list-action table-list-action--edit"
                                        title="Edit"
                                        data-testid={`BTN_org_profile_gst_row_${item.id}_edit`}
                                      >
                                        <Edit2 size={14} />
                                      </button>
                                    )}
                                    {canModify && item.workflow_state !== 'approved' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteGst(item.id);
                                        }}
                                        className="table-list-action table-list-action--delete"
                                        title="Delete"
                                        data-testid={`BTN_org_profile_gst_row_${item.id}_delete`}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {gstList.length === 0 && (
                            <div className="p-8 text-center text-gray-400 text-sm">
                              No GST details added yet.
                            </div>
                          )}
                        </div>
                      </div>

                      {/* TDS Details (One-to-Many) */}
                      <div className="org-profile-sub-card">
                        <div className="org-profile-sub-header">
                          <span className="org-profile-sub-header-label">
                            TDS / TAN Details
                          </span>
                          {canModify && (
                            <Button
                              variant="fill"
                              icon={Plus}
                              onClick={openAddTdsModal}
                              className="btn-secondary-outline"
                              data-testid="BTN_org_profile_tds_create"
                            >
                              Create
                            </Button>
                          )}
                        </div>
                        <div className="org-profile-sub-body overflow-auto max-h-64">
                          <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-100">
                              <tr>
                                <th className="px-2 py-3 w-6"></th>
                                <th className="px-5 py-3 text-xs uppercase tracking-wider">
                                  Circle
                                </th>
                                <th className="px-5 py-3 text-xs uppercase tracking-wider">
                                  TAN
                                </th>
                                <th className="px-5 py-3 w-10"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {tdsList.map((item) => (
                                <tr
                                  key={item.id}
                                  className="hover:bg-blue-50/30"
                                >
                                  <td
                                    className="px-2 py-3 w-6 cursor-pointer"
                                    onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor({ type: 'tds', record: item }); }}
                                    title="Workflow — view timeline & act"
                                    data-testid={`BTN_org_profile_tds_row_${item.id}_workflow`}
                                  >
                                    <ApprovalDot
                                      state={(item.workflow_state ?? 'draft') as any}
                                      onClick={() => setWorkflowOpenFor({ type: 'tds', record: item })}
                                    />
                                  </td>
                                  <td className="px-5 py-3 font-medium text-gray-700">
                                    {item.circle}
                                  </td>
                                  <td className="px-5 py-3 text-gray-600 text-xs">
                                    <span className="">
                                      {item.tan}
                                    </span>
                                  </td>
                                  <td
                                    className="px-5 py-3 text-right"
                                    style={{ whiteSpace: "nowrap" }}
                                  >
                                    {canModify && item.workflow_state !== 'approved' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditTdsModal(item);
                                        }}
                                        className="table-list-action table-list-action--edit"
                                        title="Edit"
                                        data-testid={`BTN_org_profile_tds_row_${item.id}_edit`}
                                      >
                                        <Edit2 size={14} />
                                      </button>
                                    )}
                                    {canModify && item.workflow_state !== 'approved' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteTds(item.id);
                                        }}
                                        className="table-list-action table-list-action--delete"
                                        title="Delete"
                                        data-testid={`BTN_org_profile_tds_row_${item.id}_delete`}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {tdsList.length === 0 && (
                            <div className="p-8 text-center text-gray-400 text-sm">
                              No TDS details added yet.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 3. Other Registrations Section */}
                    <div className="org-profile-sub-card">
                      <div className="org-profile-sub-header">
                        <span className="org-profile-sub-header-label">
                          Other Registrations / Licenses
                        </span>
                        {canModify && (
                          <Button
                            variant="fill"
                            icon={Plus}
                            onClick={openAddOtherRegModal}
                            className="btn-secondary-outline"
                            data-testid="BTN_org_profile_reg_create"
                          >
                            Create
                          </Button>
                        )}
                      </div>
                      <div className="org-profile-sub-body overflow-auto">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-100">
                            <tr>
                              <th className="px-2 py-3 w-6"></th>
                              <th className="px-5 py-3 text-xs uppercase tracking-wider w-1/4">
                                Authority
                              </th>
                              <th className="px-5 py-3 text-xs uppercase tracking-wider w-1/4">
                                Licensee
                              </th>
                              <th className="px-5 py-3 text-xs uppercase tracking-wider w-1/4">
                                Purpose
                              </th>
                              <th className="px-5 py-3 text-xs uppercase tracking-wider">
                                Expiry
                              </th>
                              <th className="px-5 py-3 w-10"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {otherRegList.map((item) => (
                              <tr key={item.id} className="hover:bg-blue-50/30">
                                <td
                                  className="px-2 py-3 w-6 cursor-pointer"
                                  onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor({ type: 'reg', record: item }); }}
                                  title="Workflow — view timeline & act"
                                  data-testid={`BTN_org_profile_reg_row_${item.id}_workflow`}
                                >
                                  <ApprovalDot
                                    state={(item.workflow_state ?? 'draft') as any}
                                    onClick={() => setWorkflowOpenFor({ type: 'reg', record: item })}
                                  />
                                </td>
                                <td className="px-5 py-3 font-medium text-gray-700">
                                  {item.authority}
                                </td>
                                <td className="px-5 py-3 text-gray-600">
                                  {item.licensee}
                                </td>
                                <td className="px-5 py-3 text-gray-600">
                                  {item.purpose}
                                </td>
                                <td className="px-5 py-3 text-gray-600">
                                  <div className="flex items-center text-xs">
                                    <span
                                      className={`w-2 h-2 rounded-full mr-2 ${item.expiry_date && new Date(item.expiry_date) < new Date() ? "bg-red-500" : "bg-green-500"}`}
                                    />
                                    {item.expiry_date ? formatDate(item.expiry_date) : "N/A"}
                                  </div>
                                </td>
                                <td
                                  className="px-5 py-3 text-right"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {canModify && item.workflow_state !== 'approved' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openEditOtherRegModal(item);
                                      }}
                                      className="table-list-action table-list-action--edit"
                                      title="Edit"
                                      data-testid={`BTN_org_profile_reg_row_${item.id}_edit`}
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                  )}
                                  {canModify && item.workflow_state !== 'approved' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteOtherReg(item.id);
                                      }}
                                      className="table-list-action table-list-action--delete"
                                      title="Delete"
                                      data-testid={`BTN_org_profile_reg_row_${item.id}_delete`}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {otherRegList.length === 0 && (
                          <div className="p-8 text-center text-gray-400 text-sm">
                            No other registrations added yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* flex flex-col gap-3 */}
                </div>
                {/* org-profile-section */}
              </>
            )}
          </div>
        </div>
      </div>

      {/* --- MODALS --- */}

      <Modal
        isOpen={isProfileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        size="lg"
        data-testid="MOD_org_profile_edit"
      >
        <Modal.Header>
          {isEditingProfile ? 'Editing' : 'Viewing'} "<span className="item-highlight">{orgDetails.name}</span>"
        </Modal.Header>
        <Modal.Body className="">
          <form
            id="profile-form"
            onSubmit={handleSaveProfile}
            className="grid grid-cols-3 gap-x-3 gap-y-3 p-3"
          >
            <ValidatedInput
              vh={profileVh}
              name="name"
              code={101}
              required
              label="Organisation Name"
              value={profileFormData.name || ""}
              onChange={(e) => {
                setProfileFormData({
                  ...profileFormData,
                  name: e.target.value,
                });
              }}
              leadingIcon={<Building2 size={14} />}
              disabled={!isEditingProfile}
              data-testid="INP_org_profile_name"
            />
            <ValidatedInput
              vh={profileVh}
              name="code"
              code={501}
              required
              label="Code"
              placeholder="Ex: ACME-01"
              value={profileFormData.code || ""}
              onChange={(e) => {
                setProfileFormData({
                  ...profileFormData,
                  code: e.target.value,
                });
              }}
              leadingIcon={<Globe size={14} />}
              disabled={!isEditingProfile}
              data-testid="INP_org_profile_code"
            />
            <ValidatedDatePicker
              vh={profileVh}
              name="incorporation_date"
              code={305}
              label="Incorporation Date"
              value={profileFormData.incorporation_date || ""}
              onChange={(e: any) =>
                setProfileFormData({
                  ...profileFormData,
                  incorporation_date: e.target.value,
                })
              }
              disabled={!isEditingProfile}
              data-testid="INP_org_profile_incorporation_date"
            />
            <ValidatedInput
              vh={profileVh}
              name="constitution"
              code={101}
              label="Constitution"
              placeholder="Ex: Private Limited"
              value={profileFormData.constitution || ""}
              onChange={(e) =>
                setProfileFormData({
                  ...profileFormData,
                  constitution: e.target.value,
                })
              }
              leadingIcon={<Shield size={14} />}
              disabled={!isEditingProfile}
              data-testid="INP_org_profile_constitution"
            />
            <ValidatedInput
              vh={profileVh}
              name="cin"
              code={511}
              required
              label="CIN Number"
              placeholder="U12345MH2020PTC123456"
              maxLength={21}
              value={profileFormData.cin || ""}
              data-testid="INP_org_profile_cin"
              disabled={!isEditingProfile}
              onChange={(e) => {
                const raw = e.target.value.toUpperCase();
                let out = "";
                for (let i = 0; i < raw.length && out.length < 21; i++) {
                  const c = raw[i];
                  const pos = out.length;
                  const isDigit = /[0-9]/.test(c);
                  const isAlpha = /[A-Z]/.test(c);
                  if (pos === 0 && (c === "L" || c === "U")) out += c;
                  else if (pos >= 1 && pos <= 5 && isDigit) out += c;
                  else if (pos >= 6 && pos <= 7 && isAlpha) out += c;
                  else if (pos >= 8 && pos <= 11 && isDigit) out += c;
                  else if (pos >= 12 && pos <= 14 && isAlpha) out += c;
                  else if (pos >= 15 && pos <= 20 && isDigit) out += c;
                }
                setProfileFormData({ ...profileFormData, cin: out });
              }}
              leadingIcon={<Hash size={14} />}
            />
            <ValidatedInput
              vh={profileVh}
              name="pan"
              code={510}
              label="PAN Number"
              placeholder="ABCDE1234F"
              maxLength={10}
              value={profileFormData.pan || ""}
              onChange={(e) => {
                setProfileFormData({ ...profileFormData, pan: e.target.value.toUpperCase() });
              }}
              leadingIcon={<CreditCard size={14} />}
              disabled={!isEditingProfile}
              data-testid="INP_org_profile_pan"
            />

            <div className="col-span-3 h-px bg-gray-100 my-1"></div>

            <ValidatedInput
              vh={profileVh}
              name="contact_person"
              code={102}
              label="Contact Person"
              placeholder="Full Name"
              value={profileFormData.contact_person || ""}
              onChange={(e) =>
                setProfileFormData({
                  ...profileFormData,
                  contact_person: e.target.value,
                })
              }
              leadingIcon={<User size={14} />}
              disabled={!isEditingProfile}
              data-testid="INP_org_profile_contact_person"
            />
            <ValidatedInput
              vh={profileVh}
              name="contact_phone"
              code={405}
              label="Contact Phone"
              placeholder="+91 99999 99999"
              maxLength={20}
              value={profileFormData.contact_phone || ""}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9+\-()\s]/g, "").slice(0, 20);
                setProfileFormData({
                  ...profileFormData,
                  contact_phone: v,
                });
              }}
              disabled={!isEditingProfile}
              data-testid="INP_org_profile_contact_phone"
            />
            <ValidatedInput
              vh={profileVh}
              name="contact_email"
              code={401}
              label="Contact Email"
              type="email"
              placeholder="person@company.com"
              value={profileFormData.contact_email || ""}
              onChange={(e) => {
                setProfileFormData({
                  ...profileFormData,
                  contact_email: e.target.value,
                });
              }}
              disabled={!isEditingProfile}
              data-testid="INP_org_profile_contact_email"
            />

            <div className="col-span-3 h-px bg-gray-100 my-1"></div>

            <div className="col-span-3 grid grid-cols-2 gap-x-3">
              <ValidatedTextarea
                vh={profileVh}
                name="address"
                code={101}
                required
                label="Registered Office Address"
                value={profileFormData.address || ""}
                onChange={(e) => {
                  setProfileFormData({
                    ...profileFormData,
                    address: e.target.value,
                  });
                }}
                disabled={!isEditingProfile}
                data-testid="INP_org_profile_address"
              />
              <ValidatedTextarea
                vh={profileVh}
                name="corporate_address"
                code={101}
                label="Corporate Office Address"
                value={profileFormData.corporate_address || ""}
                onChange={(e) =>
                  setProfileFormData({
                    ...profileFormData,
                    corporate_address: e.target.value,
                  })
                }
                disabled={!isEditingProfile}
                data-testid="INP_org_profile_corporate_address"
              />
            </div>

            <div className="col-span-3 h-px bg-gray-100 my-1"></div>
            <div
              className="col-span-3"
              style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
            >
              <input
                type="checkbox"
                id="cadence_enabled"
                checked={cadenceFormEnabled}
                onChange={(e) => setCadenceFormEnabled(e.target.checked)}
                disabled={!isEditingProfile}
                style={{ width: 16, height: 16, accentColor: "#4d288b", cursor: isEditingProfile ? "pointer" : "not-allowed" }}
                data-testid="CHK_org_profile_cadence_enabled"
              />
              <label htmlFor="cadence_enabled" style={{ cursor: "pointer", fontWeight: 600 }}>
                Cadence run — Enabled
              </label>
              <span style={{ color: "#6b7280", fontSize: 12, flexBasis: "100%" }}>
                When enabled, the cadence engine automatically runs scheduled obligations for this entity.
                Leave off to raise obligations only via manual rollout.
              </span>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          {!isEditingProfile && (
            <Button
              variant="fill"
              icon={Edit2}
              onClick={() => setIsEditingProfile(true)}
              data-testid="BTN_org_profile_edit_mode"
            >
              Edit
            </Button>
          )}
          {isEditingProfile && (
            <Button
              type="submit"
              form="profile-form"
              variant="update"
              icon={Check}
              disabled={submittingProfile}
              data-testid="BTN_org_profile_submit"
            >
              {submittingProfile ? "Updating..." : "Update"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* 2. GST Modal */}
      <Modal
        isOpen={isGstModalOpen}
        onClose={() => setGstModalOpen(false)}
        size="md"
        data-testid="MOD_org_gst_form"
      >
        <Modal.Header>
          {editingGstId ? (
            <>
              Editing "
              <span className="item-highlight">{gstFormData.gstin}</span>"
            </>
          ) : (
            "Create GST Registration"
          )}
        </Modal.Header>

        <Modal.Body>
          <form
            id="gst-form"
            onSubmit={handleSaveGst}
            className="grid grid-cols-2 gap-x-6 gap-y-4"
          >
            <ValidatedInput
              vh={gstVh}
              name="gstin"
              code={512}
              required
              label="GSTIN Number"
              placeholder="Ex: 36ABCDE1234F1Z5"
              maxLength={15}
              disabled={!gstFormEnabled}
              value={gstFormData.gstin || ""}
              data-testid="INP_org_gst_gstin"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15);
                setGstFormData({ ...gstFormData, gstin: v });
              }}
            />
            <ValidatedInput
              vh={gstVh}
              name="state"
              code={101}
              required
              label="State"
              placeholder="Ex: Telangana"
              disabled={!gstFormEnabled}
              value={gstFormData.state || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setGstFormData({ ...gstFormData, state: e.target.value })
              }
              data-testid="INP_org_gst_state"
            />

            <div className="col-span-3 h-px bg-gray-100 my-1"></div>

            <ValidatedInput
              vh={gstVh}
              name="auth_person"
              code={102}
              required
              label="Authorised Person Name"
              placeholder="Full Name"
              disabled={!gstFormEnabled}
              value={gstFormData.auth_person || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setGstFormData({ ...gstFormData, auth_person: e.target.value })
              }
              data-testid="INP_org_gst_auth_person"
            />
            <ValidatedDatePicker
              vh={gstVh}
              name="reg_date"
              code={305}
              required
              label="Date of Registration"
              disabled={!gstFormEnabled}
              value={gstFormData.reg_date || ""}
              onChange={(e: any) =>
                setGstFormData({ ...gstFormData, reg_date: e.target.value })
              }
              data-testid="INP_org_gst_reg_date"
            />

            <ValidatedInput
              vh={gstVh}
              name="auth_phone"
              code={405}
              label="Phone Number"
              placeholder="+91 99999 99999"
              maxLength={20}
              disabled={!gstFormEnabled}
              value={gstFormData.auth_phone || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.replace(/[^0-9+\-()\s]/g, "").slice(0, 20);
                setGstFormData({ ...gstFormData, auth_phone: v });
              }}
              data-testid="INP_org_gst_auth_phone"
            />
            <ValidatedInput
              vh={gstVh}
              name="auth_email"
              code={401}
              label="Email Address"
              type="email"
              placeholder="person@company.com"
              disabled={!gstFormEnabled}
              value={gstFormData.auth_email || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setGstFormData({ ...gstFormData, auth_email: e.target.value })
              }
              data-testid="INP_org_gst_auth_email"
            />
            <ValidatedTextarea
              vh={gstVh}
              name="address"
              code={101}
              label="Address"
              placeholder="Full Registered Address for this GSTIN"
              className="col-span-3"
              disabled={!gstFormEnabled}
              value={gstFormData.address || ""}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setGstFormData({ ...gstFormData, address: e.target.value })
              }
              data-testid="INP_org_gst_address"
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          {editingGstId && !gstFormEnabled && (
            <Button
              variant="fill"
              icon={Edit2}
              onClick={() => setGstFormEnabled(true)}
              data-testid="BTN_org_gst_edit"
            >
              Edit
            </Button>
          )}
          {editingGstId && gstFormEnabled && (
            <Button
              type="submit"
              form="gst-form"
              variant="update"
              icon={Check}
              disabled={submittingGst}
              data-testid="BTN_org_gst_submit"
            >
              {submittingGst ? "Updating..." : "Update"}
            </Button>
          )}
          {!editingGstId && (
            <Button
              type="submit"
              form="gst-form"
              variant="fill"
              icon={Save}
              disabled={submittingGst}
              data-testid="BTN_org_gst_submit"
            >
              {submittingGst ? "Submitting..." : "Submit"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* 3. TDS Modal */}
      <Modal
        isOpen={isTdsModalOpen}
        onClose={() => setTdsModalOpen(false)}
        size="md"
        data-testid="MOD_org_tds_form"
      >
        <Modal.Header>
          {editingTdsId ? (
            <>
              Editing "<span className="item-highlight">{tdsFormData.tan}</span>
              "
            </>
          ) : (
            "Create TDS / TAN Details"
          )}
        </Modal.Header>
        <Modal.Body>
          <form
            id="tds-form"
            onSubmit={handleSaveTds}
            className="grid grid-cols-2 gap-x-6 gap-y-4"
          >
            <ValidatedInput
              vh={tdsVh}
              name="tan"
              code={513}
              required
              label="TAN Number"
              placeholder="Ex: HYDA01234B"
              maxLength={10}
              disabled={!tdsFormEnabled}
              value={tdsFormData.tan || ""}
              data-testid="INP_org_tds_tan"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 10);
                setTdsFormData({ ...tdsFormData, tan: v });
              }}
            />
            <ValidatedInput
              vh={tdsVh}
              name="circle"
              code={101}
              required
              label="Circle"
              placeholder="Ex: Hyderabad"
              disabled={!tdsFormEnabled}
              value={tdsFormData.circle || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTdsFormData({ ...tdsFormData, circle: e.target.value })
              }
              data-testid="INP_org_tds_circle"
            />

            <div className="col-span-3 h-px bg-gray-100 my-1"></div>

            <ValidatedInput
              vh={tdsVh}
              name="auth_person"
              code={102}
              required
              label="Authorised Person Name"
              placeholder="Full Name"
              disabled={!tdsFormEnabled}
              value={tdsFormData.auth_person || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTdsFormData({ ...tdsFormData, auth_person: e.target.value })
              }
              data-testid="INP_org_tds_auth_person"
            />
            <ValidatedDatePicker
              vh={tdsVh}
              name="reg_date"
              code={305}
              required
              label="Date of Registration"
              disabled={!tdsFormEnabled}
              value={tdsFormData.reg_date || ""}
              onChange={(e: any) =>
                setTdsFormData({ ...tdsFormData, reg_date: e.target.value })
              }
              data-testid="INP_org_tds_reg_date"
            />

            <ValidatedInput
              vh={tdsVh}
              name="auth_phone"
              code={405}
              label="Phone Number"
              placeholder="+91 99999 99999"
              maxLength={20}
              disabled={!tdsFormEnabled}
              value={tdsFormData.auth_phone || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.replace(/[^0-9+\-()\s]/g, "").slice(0, 20);
                setTdsFormData({ ...tdsFormData, auth_phone: v });
              }}
              data-testid="INP_org_tds_auth_phone"
            />
            <ValidatedInput
              vh={tdsVh}
              name="auth_email"
              code={401}
              label="Email Address"
              type="email"
              placeholder="person@company.com"
              disabled={!tdsFormEnabled}
              value={tdsFormData.auth_email || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTdsFormData({ ...tdsFormData, auth_email: e.target.value })
              }
              data-testid="INP_org_tds_auth_email"
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          {editingTdsId && !tdsFormEnabled && (
            <Button
              variant="fill"
              icon={Edit2}
              onClick={() => setTdsFormEnabled(true)}
              data-testid="BTN_org_tds_edit"
            >
              Edit
            </Button>
          )}
          {editingTdsId && tdsFormEnabled && (
            <Button
              type="submit"
              form="tds-form"
              variant="update"
              icon={Check}
              disabled={submittingTds}
              data-testid="BTN_org_tds_submit"
            >
              {submittingTds ? "Updating..." : "Update"}
            </Button>
          )}
          {!editingTdsId && (
            <Button
              type="submit"
              form="tds-form"
              variant="fill"
              icon={Save}
              disabled={submittingTds}
              data-testid="BTN_org_tds_submit"
            >
              {submittingTds ? "Submitting..." : "Submit"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* 4. Other Registrations Modal (NEW) */}
      <Modal
        isOpen={isOtherRegModalOpen}
        onClose={() => setOtherRegModalOpen(false)}
        size="md"
        data-testid="MOD_org_reg_form"
      >
        <Modal.Header>
          {editingOtherRegId ? (
            <>
              Editing "
              <span className="item-highlight">
                {otherRegFormData.authority}
              </span>
              "
            </>
          ) : (
            "Create Other Registration"
          )}
        </Modal.Header>
        <Modal.Body>
          <form
            id="other-reg-form"
            onSubmit={handleSaveOtherReg}
            className="grid grid-cols-3 gap-x-6 gap-y-4"
          >
            <ValidatedInput
              vh={regVh}
              name="authority"
              code={101}
              required
              label="Name of Authority"
              placeholder="Ex: FSSAI, ISO, Municipal Corp"
              disabled={!otherRegFormEnabled}
              value={otherRegFormData.authority || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOtherRegFormData({ ...otherRegFormData, authority: e.target.value })
              }
              data-testid="INP_org_reg_authority"
            />
            <ValidatedInput
              vh={regVh}
              name="licensee"
              code={101}
              required
              label="Licensee Name"
              placeholder="Entity/Unit Name"
              disabled={!otherRegFormEnabled}
              value={otherRegFormData.licensee || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOtherRegFormData({ ...otherRegFormData, licensee: e.target.value })
              }
              data-testid="INP_org_reg_licensee"
            />
            <ValidatedInput
              vh={regVh}
              name="purpose"
              code={101}
              required
              label="Purpose / License Type"
              placeholder="Ex: Trade License"
              disabled={!otherRegFormEnabled}
              value={otherRegFormData.purpose || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOtherRegFormData({ ...otherRegFormData, purpose: e.target.value })
              }
              data-testid="INP_org_reg_purpose"
            />
            <ValidatedDatePicker
              vh={regVh}
              name="issue_date"
              code={305}
              required
              label="Issue Date"
              disabled={!otherRegFormEnabled}
              value={otherRegFormData.issue_date || ""}
              onChange={(e: any) =>
                setOtherRegFormData({ ...otherRegFormData, issue_date: e.target.value })
              }
              data-testid="INP_org_reg_issue_date"
            />
            <ValidatedDatePicker
              vh={regVh}
              name="expiry_date"
              code={305}
              label="Expiry Date"
              disabled={!otherRegFormEnabled}
              value={otherRegFormData.expiry_date || ""}
              onChange={(e: any) =>
                setOtherRegFormData({ ...otherRegFormData, expiry_date: e.target.value })
              }
              data-testid="INP_org_reg_expiry_date"
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          {editingOtherRegId && !otherRegFormEnabled && (
            <Button
              variant="fill"
              icon={Edit2}
              onClick={() => setOtherRegFormEnabled(true)}
              data-testid="BTN_org_reg_edit"
            >
              Edit
            </Button>
          )}
          {editingOtherRegId && otherRegFormEnabled && (
            <Button
              type="submit"
              form="other-reg-form"
              variant="update"
              icon={Check}
              disabled={submittingOtherReg}
              data-testid="BTN_org_reg_submit"
            >
              {submittingOtherReg ? "Updating..." : "Update"}
            </Button>
          )}
          {!editingOtherRegId && (
            <Button
              type="submit"
              form="other-reg-form"
              variant="fill"
              icon={Save}
              disabled={submittingOtherReg}
              data-testid="BTN_org_reg_submit"
            >
              {submittingOtherReg ? "Submitting..." : "Submit"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <WorkflowModal
        isOpen={!!workflowOpenFor}
        onClose={closeWfModal}
        kind={workflowOpenFor
          ? workflowOpenFor.type === 'gst' ? 'org_entity_gst'
          : workflowOpenFor.type === 'tds' ? 'org_entity_tds'
          : 'org_entity_registration'
          : null}
        id={workflowOpenFor ? Number(workflowOpenFor.record.id) : null}
        onChanged={fetchData}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default OrganizationProfile;
